import { Settings2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Skeleton } from '@/components/ui/skeleton'
import { Field } from '@/components/common/FormBits'
import { useMeetingFileSettings, useSaveMeetingFileSettings, type FileSettings } from '../meetingFiles'
import { toastError } from '../ui'

/** Toplantı dosyası üst sınırı (en fazla 2 GB) ve saklama süreleri; süre dolunca dosya otomatik silinir (0 = süresiz). */
export function MeetingFileSettingsButton() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Settings2 />
        {t('crm.files.settings')}
      </Button>
      {open ? <SettingsModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const q = useMeetingFileSettings()
  return (
    <Modal open size="md" onOpenChange={(v) => !v && onClose()} icon={<Settings2 />} title={t('crm.files.settings')} description={t('crm.files.settingsHint')}>
      {q.data ? <SettingsForm initial={q.data} onClose={onClose} /> : <Skeleton className="h-40" />}
    </Modal>
  )
}

function SettingsForm({ initial, onClose }: { initial: FileSettings; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveMeetingFileSettings()
  const [v, setV] = useState({
    maxFileMb: String(initial.maxFileMb),
    recordingRetentionDays: String(initial.recordingRetentionDays),
    attachmentRetentionDays: String(initial.attachmentRetentionDays),
  })
  const n = (x: string) => Math.trunc(Number(x))
  const invalid = !(n(v.maxFileMb) >= 1 && n(v.maxFileMb) <= 2048) || [v.recordingRetentionDays, v.attachmentRetentionDays].some((x) => !(n(x) >= 0 && n(x) <= 3650))
  return (
    <div className="space-y-4">
      <Field id="fs-max" label={t('crm.files.maxFile')} hint={t('crm.files.maxFileHint')}>
        <NumberInput id="fs-max" value={v.maxFileMb} onChange={(x) => setV({ ...v, maxFileMb: x })} suffix="MB" />
      </Field>
      <Field id="fs-rec" label={t('crm.files.recordingRetention')} hint={t('crm.files.retentionHint')}>
        <NumberInput id="fs-rec" value={v.recordingRetentionDays} onChange={(x) => setV({ ...v, recordingRetentionDays: x })} suffix={t('crm.files.days')} />
      </Field>
      <Field id="fs-att" label={t('crm.files.attachmentRetention')} hint={t('crm.files.retentionHint')}>
        <NumberInput id="fs-att" value={v.attachmentRetentionDays} onChange={(x) => setV({ ...v, attachmentRetentionDays: x })} suffix={t('crm.files.days')} />
      </Field>
      {invalid ? <p className="text-xs text-destructive">{t('crm.files.settingsInvalid')}</p> : null}
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={invalid || save.isPending}
          onClick={() =>
            save.mutate(
              { maxFileMb: n(v.maxFileMb), recordingRetentionDays: n(v.recordingRetentionDays), attachmentRetentionDays: n(v.attachmentRetentionDays) },
              {
                onSuccess: () => {
                  toast.success(t('crm.files.settingsSaved'))
                  onClose()
                },
                onError: toastError,
              },
            )
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
